// context/AuthContext.js
// Global auth state for the app.
// Stores the JWT in localStorage and the user object in React state.
// Exposes: user, loading, login, register, googleLogin, logout

import { createContext, useContext, useState, useEffect } from "react";
import * as api from "../services/api";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  // user: null when logged out, { id, name, email, role, avatar } when logged in
  const [user,    setUser]    = useState(null);
  // loading: true while we're checking the stored token on first mount
  const [loading, setLoading] = useState(true);

  // ── On mount: restore session from stored JWT ──────────────────────
  // If a token exists in localStorage, verify it with the server (/api/auth/me).
  // This keeps users logged in across page refreshes.
  useEffect(() => {
    const token = localStorage.getItem("qs_token");
    if (!token) { setLoading(false); return; }

    api.getMe()
      .then(({ user }) => setUser(user))
      .catch(() => {
        // Token is invalid or expired — clear it
        localStorage.removeItem("qs_token");
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Helper: save token and update user state ───────────────────────
  const handleAuthResponse = ({ token, user }) => {
    localStorage.setItem("qs_token", token); // persist JWT
    setUser(user);
  };

  // ── register ────────────────────────────────────────────────────────
  // Calls POST /api/auth/register, stores token and user.
  const register = async (name, email, password, instituteName, educationLevel) => {
    const res = await api.register(name, email, password, instituteName, educationLevel);
    handleAuthResponse(res);
    return res.user;
  };

  const updateProfile = async (instituteName, educationLevel) => {
    const res = await api.updateProfile(instituteName, educationLevel);
    setUser(res.user);
    return res.user;
  };

  // ── login ────────────────────────────────────────────────────────────
  // Calls POST /api/auth/login.
  const login = async (email, password) => {
    const res = await api.login(email, password);
    handleAuthResponse(res);
    return res.user;
  };

  // ── googleLogin ──────────────────────────────────────────────────────
  // Called with the raw Google ID token after the user completes Google sign-in.
  // The server verifies the token, then returns our own JWT.
  const googleLogin = async (credential) => {
    const res = await api.googleLogin(credential);
    handleAuthResponse(res);
    return res.user;
  };

  // ── logout ───────────────────────────────────────────────────────────
  const logout = () => {
    localStorage.removeItem("qs_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, updateProfile, googleLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook — use this in any component: const { user, login } = useAuth()
export const useAuth = () => useContext(AuthContext);
